import React from 'react';
import type { LanguageOption } from '@ditioas/web-ui';
import { LanguageSelector } from '@ditioas/web-ui';
import { useTranslation } from 'react-i18next';

import { LocalStorageManager } from 'libs/utils';
import type { LocaleId } from 'model/locale';
import {
  defaultLocale,
  findLocaleById,
  getLocaleOptions,
  localeExists,
} from 'model/locale';

interface LocalLanguageSelectProps {
  /** The language the calling screen renders in. */
  value: LocaleId;
  onChange: (localeId: LocaleId) => void;
  className?: string;
}

/**
 * Screen-local language state for the pages an anonymous reader opens from an
 * email link, and its own `anonymousLanguageId` slot in local storage —
 * separate from the app-wide `languageId`, so a reader's pick here survives the
 * next visit without ever becoming the language of the app itself.
 *
 * Returns `[language, setLanguage, anonymousLanguageId]`:
 * - `language` — what the screen renders in; feed it to
 *   `useTranslation(ns, { lng })` and to <LocalLanguageSelect value>.
 * - `setLanguage` — switches the screen and persists the pick.
 * - `anonymousLanguageId` — the raw stored pick, `null` until one is made.
 *
 * Until a pick exists the screen tracks the live app language rather than
 * snapshotting it at mount, so it still follows a language applied later — a
 * signed-in user's profile language, or a TopBar switch.
 */
export const useAnonymousLanguage = (): [
  LocaleId,
  (localeId: LocaleId) => void,
  string | null,
] => {
  const { i18n } = useTranslation();
  const anonymousLanguageId = LocalStorageManager.getAnonymousLanguageId();
  const [pickedLanguage, setPickedLanguage] = React.useState<LocaleId | null>(
    () => (localeExists(anonymousLanguageId) ? anonymousLanguageId : null)
  );

  const appLanguage = localeExists(i18n.language)
    ? i18n.language
    : defaultLocale;

  const setLanguage = React.useCallback(
    (localeId: LocaleId): void => {
      LocalStorageManager.setAnonymousLanguageId(localeId);
      setPickedLanguage(localeId);
    },
    [setPickedLanguage]
  );

  return [pickedLanguage ?? appLanguage, setLanguage, anonymousLanguageId];
};

/**
 * Language picker that switches a single screen's language instead of the app's.
 *
 * For pages rendered outside the app Layout — the anonymous, email-linked
 * read-only views — which have no TopBar selector and whose reader is often not
 * a Ditio user at all. Nothing global is touched: no `i18n.changeLanguage`, no
 * cached device language, no user profile write. The caller keeps the language
 * in state and feeds it back through `useTranslation(ns, { lng })`.
 */
const LocalLanguageSelect = ({
  value,
  onChange,
  className,
}: LocalLanguageSelectProps) => {
  // Bound to `value` as well, so the selector's own label follows the screen.
  const { t, i18n } = useTranslation(undefined, { lng: value });

  // `useTranslation({ lng })` renders fallback-language strings while the target
  // bundle is still missing, so load it before switching the screen over.
  const handleLanguageChange = async (
    newLanguage: LanguageOption
  ): Promise<void> => {
    const localeId = localeExists(newLanguage.id)
      ? newLanguage.id
      : defaultLocale;

    await i18n.loadLanguages(localeId);
    onChange(localeId);
  };

  return (
    <LanguageSelector
      value={findLocaleById(value)}
      onChange={handleLanguageChange}
      options={getLocaleOptions(value)}
      triggerProps={{ 'aria-label': t('General.Language') }}
      containerProps={{ className }}
    />
  );
};

export default LocalLanguageSelect;
